import AdminLayout from '@/components/admin/AdminLayout'
import BranchDialog from '@/components/admin/BranchDialog'
import BranchList from '@/components/admin/BranchList'
import BranchSettingsDialog from '@/components/admin/BranchSettingsDialog'
import DeleteBranchDialog from '@/components/admin/DeleteBranchDialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Branch,
  CreateBranchInput,
  UpdateBranchInput,
  useBranches,
} from '@/queries/useBranches'
import { useUser } from '@clerk/clerk-react'
import { Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

export default function Branches() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const { user, isLoaded: isUserLoaded } = useUser()
  const {
    branches,
    createBranch,
    updateBranch,
    deleteBranch,
  } = useBranches()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null)

  // Check for restaurant_admin or super_admin role in user metadata (aligned with backend check)
  const userRoles = (user?.publicMetadata?.roles as string[] | undefined) || []
  const isRestaurantAdmin =
    Array.isArray(userRoles) &&
    (userRoles.includes('restaurant_admin') || userRoles.includes('super_admin'))

  // Redirect if not restaurant admin or super admin
  useEffect(() => {
    if (isUserLoaded && !isRestaurantAdmin) {
      navigate('/admin/dashboard', { replace: true })
    }
  }, [isUserLoaded, isRestaurantAdmin, navigate])

  // Show loading state while checking user
  if (!isUserLoaded) {
    return (
      <AdminLayout>
        <div
          className="container mx-auto max-w-7xl p-[var(--spacing-lg)]"
          data-testid="branches-page-loading"
        >
          <p>Loading...</p>
        </div>
      </AdminLayout>
    )
  }

  // Show access denied if not restaurant admin or super admin
  if (!isRestaurantAdmin) {
    return (
      <AdminLayout>
        <div
          className="container mx-auto max-w-7xl p-[var(--spacing-lg)]"
          data-testid="branches-page-access-denied"
        >
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>{t('branches.accessDenied.title')}</AlertTitle>
            <AlertDescription>
              {t('branches.accessDenied.message')}
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    )
  }

  const handleCreate = async (data: CreateBranchInput | UpdateBranchInput) => {
    try {
      await createBranch.mutateAsync(data as CreateBranchInput)
      setCreateDialogOpen(false)
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to create branch:', error)
    }
  }

  const handleEdit = (branch: Branch) => {
    setSelectedBranch(branch)
    setEditDialogOpen(true)
  }

  const handleUpdate = async (data: UpdateBranchInput) => {
    if (!selectedBranch) return
    try {
      await updateBranch.mutateAsync(selectedBranch.id, data)
      setEditDialogOpen(false)
      setSelectedBranch(null)
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to update branch:', error)
    }
  }

  const handleSettings = (branch: Branch) => {
    setSelectedBranch(branch)
    setSettingsDialogOpen(true)
  }

  const handleSettingsUpdate = async (data: UpdateBranchInput) => {
    if (!selectedBranch) return
    try {
      await updateBranch.mutateAsync(selectedBranch.id, data)
      setSettingsDialogOpen(false)
      setSelectedBranch(null)
    } catch (error) {
      // Error is handled by the form component
      console.error('Failed to update branch settings:', error)
    }
  }

  const handleDelete = (branch: Branch) => {
    setSelectedBranch(branch)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedBranch) return
    try {
      await deleteBranch.mutateAsync(selectedBranch.id)
      setDeleteDialogOpen(false)
      setSelectedBranch(null)
    } catch (error) {
      // Error is handled by the dialog component
      console.error('Failed to delete branch:', error)
    }
  }

  return (
    <AdminLayout>
      <div
        className="container mx-auto max-w-[1400px] p-[var(--spacing-lg)]"
        data-testid="branches-page"
      >
        {/* Page Header */}
        <div className="mb-[var(--spacing-lg)]">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-[var(--text-3xl)] font-bold mb-[var(--spacing-sm)] font-[var(--font-heading)] text-[var(--text-primary)]"
                data-testid="branches-page-title"
              >
                {t('branches.title')}
              </h1>
              <p
                className="text-[var(--text-secondary)] text-[var(--text-base)] font-[var(--font-body)]"
                data-testid="branches-page-subtitle"
              >
                {t('branches.subtitle')}
              </p>
            </div>
            <Button
              onClick={() => setCreateDialogOpen(true)}
              data-testid="branches-page-add-button"
            >
              <Plus className="h-4 w-4 mr-[var(--spacing-xs)] rtl:mr-0 rtl:ml-[var(--spacing-xs)]" />
              {t('branches.addBranch')}
            </Button>
          </div>
        </div>

        {/* Branch List */}
        <BranchList
          branches={branches.data}
          isLoading={branches.isLoading}
          error={branches.error}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onSettings={handleSettings}
        />

        {/* Create Dialog */}
        <BranchDialog
          branch={null}
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onSubmit={handleCreate}
          isLoading={createBranch.isLoading}
          error={createBranch.error}
        />

        {/* Edit Dialog */}
        <BranchDialog
          branch={selectedBranch}
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open)
            if (!open) setSelectedBranch(null)
          }}
          onSubmit={handleUpdate}
          isLoading={updateBranch.isLoading}
          error={updateBranch.error}
        />

        {/* Settings Dialog */}
        {selectedBranch && (
          <BranchSettingsDialog
            branch={selectedBranch}
            open={settingsDialogOpen}
            onOpenChange={(open) => {
              setSettingsDialogOpen(open)
              if (!open) setSelectedBranch(null)
            }}
            onSubmit={handleSettingsUpdate}
            isLoading={updateBranch.isLoading}
            error={updateBranch.error}
          />
        )}

        {/* Delete Dialog */}
        <DeleteBranchDialog
          branch={selectedBranch}
          open={deleteDialogOpen}
          onOpenChange={(open) => {
            setDeleteDialogOpen(open)
            if (!open) setSelectedBranch(null)
          }}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteBranch.isLoading}
          error={deleteBranch.error}
        />
      </div>
    </AdminLayout>
  )
}

